import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import googleImage from '../../assets/images/google.png';
import appleImage from '../../assets/images/apple_logo.png';
import darkAppleImage from '../../assets/images-night/apple_logo.png';
import emailIcon from '../../assets/images/email.png';
import padlockIcon from '../../assets/images/padlock.png';
import { useThemeContext } from '../../contexts/ThemeContext';
import iconLauncher from '../../assets/images/ic_launcher_round.png';
import darkIconLauncher from '../../assets/images-night/ic_launcher_round.png';
import { useAuth } from '../../hooks/useAuth';
import { googleAuth } from '../../utils/googleAuth';

interface LoginFormProps {
  onLoginSuccess?: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onLoginSuccess }) => {
  const { isDark } = useThemeContext();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { login, loginWithGoogle, isLoading, error } = useAuth();

  // Initialize Google Auth on component mount (loads script, no UI rendering)
  useEffect(() => {
    googleAuth.loadScript().catch((err) =>
      console.error('Failed to load Google Auth script:', err)
    );
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    const result = await login({ email, password });
    if (result.success) {
      onLoginSuccess?.();
    }
  };

  const handleGoogleLogin = () => {
    // Use the One Tap prompt instead of hidden button click
    googleAuth.prompt(async (credential: string) => {
      const result = await loginWithGoogle(credential);
      if (result.success) {
        onLoginSuccess?.();
      }
    });
  };

  const handleAppleLogin = () => {
    toast.info('Apple Sign-In is not available at the moment.');
  };

  return (
    <div className="w-full flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-light p-8 rounded-lg shadow-md">
        <div className="flex items-center justify-between space-x-2">
          <h2 className="text-2xl font-medium text-dark">Sign In</h2>
          <img
            src={isDark ? iconLauncher : darkIconLauncher}
            alt="App Icon"
            style={{ width:50, height:50 }}
            className="border rounded-lg border-bg_grey_light"
          />
        </div>
        
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        <div className="mb-4">
          <div className="text-dark">
            Sign In with Open account
          </div>
          <div className='flex space-x-4 text-sm'>
            {/* Google button */}
            <Button 
              variant="google" 
              className="w-full mt-2"
              onClick={handleGoogleLogin}
              disabled={isLoading}
            >
              <img
                src={googleImage}
                alt="Google Icon"
                className="w-5 h-5 mr-2 inline-block align-middle"
              />
              <span className="align-middle text-dark">Google</span>
            </Button>

            {/* Apple button */}
            <Button 
              variant="google" 
              className="w-full mt-2"
              onClick={handleAppleLogin}
              disabled={isLoading}
            >
              <img
                src={isDark ? darkAppleImage : appleImage}
                alt="Apple Icon"
                className="w-5 h-5 mr-2 inline-block align-middle"
              />
              <span className="align-middle text-dark">Apple</span>
            </Button>

          </div>
        </div>

        {/* Divider */}
        <div className="flex items-center my-2">
          <hr className="flex-grow border-t border-grey_light" />
          <span className="mx-4 text-grey_light">or</span>
          <hr className="flex-grow border-t border-grey_light" />
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="text-dark">
            Or continue with email address
          </div>
          <div>
            <label htmlFor="email" className="block text-sm font-medium mb-1 text-dark">
              Email Address
            </label>
            <Input
              leftIcon={emailIcon}
              attributes={{
                id: "email",
                type: "email",
                placeholder: "Enter your email",
                value: email,
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value),
                required: true,
                disabled: isLoading
              }}
              className="w-full"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium mb-1 text-dark">
              Password
            </label>
            <Input
              leftIcon={padlockIcon}
              showPasswordToggle={true}
              attributes={{
                id: "password",
                placeholder: "Enter your password",
                value: password,
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value),
                required: true,
                disabled: isLoading
              }}
              className="w-full"
              type="password"
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className={`w-full py-2 px-4 rounded-lg font-medium transition-colors duration-200 ${isLoading
                ? 'bg-grey_light cursor-not-allowed text-grey_dark'
                : 'bg-primary hover:bg-primary-variant text-white'
              }`}
          >
            {isLoading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>

        <div className="text-center mt-2">
          <p className="text-dark">
            <Link
              to="/forgot-password"
              className="text-primary hover:text-primary-variant font-medium transition-colors duration-200"
            >
              Forgot Password
            </Link>
          </p>
        </div>

        <div className="text-center mt-3">
          <p className="text-dark">
            Don't have an account?{' '}
            <Link
              to="/signup"
              className="text-primary hover:text-primary-variant font-medium transition-colors duration-200"
            >
              Sign up here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;